import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAppSelector } from './redux';
import { hasPermissionByRole } from '../constants/Authorization.constants';
import {
  AuthorizationPermission,
  AuthorizationRole
} from '../types/Authorization.types';
import { selectIsPlatformAdmin } from '../store/selectors/authSelectors';

export const useAuthorization = () => {
  const { id: routeAiSystemId } = useParams<{ id: string }>();
  const authUserId = useAppSelector((state) => state.auth.user?.id);
  const authUserEmail = useAppSelector((state) => state.auth.user?.email);
  const isPlatformAdmin = useAppSelector(selectIsPlatformAdmin);

  const currentOverview = useAppSelector(
    (state) => state.aiSystems.currentOverview
  );
  const currentOverviewAiSystemId = useAppSelector(
    (state) => state.aiSystems.currentOverviewAiSystemId
  );

  const aiSystemRole: AuthorizationRole | null = useMemo(() => {
    const targetId = routeAiSystemId;
    if (!targetId || (!authUserId && !authUserEmail) || !currentOverview)
      return null;
    if (currentOverviewAiSystemId !== targetId) return null;

    const roleSections: Array<{
      key: keyof typeof currentOverview.users;
      role: AuthorizationRole;
    }> = [
      { key: 'owner', role: 'OWNER' },
      { key: 'admin', role: 'ADMIN' },
      { key: 'editor', role: 'EDITOR' },
      { key: 'viewer', role: 'VIEWER' }
    ];

    const matched = roleSections.find(({ key }) =>
      currentOverview.users[key].some(
        (user) =>
          (authUserId && user.id === authUserId) ||
          (authUserEmail && user.email === authUserEmail)
      )
    );

    return matched?.role ?? null;
  }, [
    routeAiSystemId,
    authUserId,
    authUserEmail,
    currentOverview,
    currentOverviewAiSystemId
  ]);

  const effectiveRole: AuthorizationRole | null = isPlatformAdmin
    ? 'ADMIN'
    : aiSystemRole;
  const roleResolved = effectiveRole !== null;

  const can = useCallback(
    (permission: AuthorizationPermission) => {
      if (!effectiveRole) {
        return false;
      }
      return hasPermissionByRole(effectiveRole, permission);
    },
    [effectiveRole]
  );

  return {
    aiSystemRole,
    effectiveRole,
    isPlatformAdmin,
    roleResolved,
    can
  };
};
